import {
  opportunitiesPipelinesCreateMutation,
  opportunitiesPipelinesDestroyMutation,
  opportunitiesPipelinesListOptions,
  opportunitiesPipelinesListQueryKey,
  opportunitiesPipelinesPartialUpdateMutation,
  opportunitiesPipelinesRetrieveOptions,
  PaginatedReturnOpportunityPipelineNomenclatorListReadable,
  ReturnOpportunityPipelineNomenclatorReadable
} from '@crm/api'
import { ConfirmModal, DndTable } from '@crm/components'
import {
  CatalogActions,
  CatalogAddButton,
  FormModal,
  FormNomenclaturesOpportunityPipelines
} from '@crm/features/nomenclatures/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useCapabilities } from '@crm/hooks/useCapabilities'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { paths } from '@crm/routes'
import { defaultFilters } from '@crm/utils'
import { ConditionalText, DayLabel } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { getFullName, showApiErrors } from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Form, MenuProps, TableColumnsType } from 'antd'
import { Edit, Status, Trash } from 'iconsax-react'

import { useCallback, useEffect, useMemo, useState } from 'react'

const formName = 'opportunity-pipelines'

export const NomenclaturesOpportunityPipelines = () => {
  const { canManageCatalogs } = useCapabilities()

  const { t } = useLocales()
  const params = useQueryParams()

  const sorter = useTableSorter()

  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [isCreate, setIsCreate] = useState(false)
  const navigate = useAppNavigate()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()
  const queryClient = useQueryClient()
  const { notification } = App.useApp()

  const [sorted, setSorted] = useState(false)
  const [data, setData] =
    useState<PaginatedReturnOpportunityPipelineNomenclatorListReadable>()

  const [form] = Form.useForm()
  const [showResponsibleWarning, setShowResponsibleWarning] = useState(false)

  const { data: opportunityPipelines, isLoading } = useQuery(
    opportunitiesPipelinesListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  const createMutation = useMutation({
    ...opportunitiesPipelinesCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([opportunitiesPipelinesListQueryKey()])
      queryClient.refetchQueries()

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const updateMutation = useMutation({
    ...opportunitiesPipelinesPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([opportunitiesPipelinesListQueryKey()])
      queryClient.refetchQueries()

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  useEffect(() => {
    if (!opportunityPipelines) return
    setData(opportunityPipelines)
  }, [opportunityPipelines])

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        icon: <Edit size={20} />,
        key: 'edit',
        onClick: () => setEditItem(id)
      },
      {
        label: t('nomenclature.edit_statuses'),
        icon: <Status size={20} />,
        key: 'edit_statuses',
        onClick: () =>
          navigate(paths.admin.nomenclatures.opportunities.pipeline(String(id)))
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItem(id)
      }
    ],
    [navigate, t]
  )

  const columns: TableColumnsType<ReturnOpportunityPipelineNomenclatorReadable> =
    useMemo(
      () => [
        {
          title: t('nomenclature.name'),
          dataIndex: 'name',
          key: 'name',
          sorter: true
        },
        {
          title: t('nomenclature.code'),
          dataIndex: 'code',
          key: 'code',
          sorter: true
        },
        {
          title: t('nomenclature.order'),
          dataIndex: 'order',
          key: 'order',
          sorter: true
        },
        {
          title: t('nomenclature.responsible'),
          dataIndex: 'responsible',
          key: 'responsible',
          sorter: true,
          render: (responsible) => (
            <ConditionalText condition={!!responsible}>
              {getFullName(responsible)}
            </ConditionalText>
          )
        },
        {
          title: t('nomenclature.is_default'),
          key: 'is_default',
          sorter: true,
          render: ({ is_default }) =>
            is_default ? t('buttons.yes') : t('buttons.no')
        },
        {
          title: t('filter.created_at'),
          dataIndex: 'created_at',
          key: 'created_at',
          sorter: true,
          render: (created_at) => <DayLabel date={created_at} />
        },
        {
          title: t('lead.modified_at'),
          dataIndex: 'modified_at',
          key: 'modified_at',
          sorter: true,
          render: (modified_at) => <DayLabel date={modified_at} />
        },
        {
          width: 50,
          fixed: 'right',
          key: 'actions',
          render: ({ id }) => (
            <CatalogActions options={getActionColumnItem(id)} />
          )
        }
      ],
      [t, getActionColumnItem]
    )

  useEffect(() => {
    if (data?.results && sorted) {
      setData(
        (prevState) =>
          ({
            ...prevState,
            results: data?.results?.map(({ id, ...params }, i) => {
              if (
                i + 1 !==
                opportunityPipelines?.results?.find((item) => item.id === id)
                  ?.order
              ) {
                updateMutation.mutate({
                  path: { id },
                  body: {
                    ...params,
                    responsible: params.responsible?.id,
                    order: i + 1
                  }
                })

                return { id, ...params, order: i + 1 }
              }

              return { id, ...params }
            })
          } as PaginatedReturnOpportunityPipelineNomenclatorListReadable)
      )
      setSorted(false)
    }
  }, [data, opportunityPipelines?.results, sorted, updateMutation])

  const deleteOpportunityPipelineMutation = useMutation({
    ...opportunitiesPipelinesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([opportunitiesPipelinesListQueryKey()])
      resetPagination()
      onCloseModal()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onCloseModal = () => {
    setDeleteItem(undefined)
    setEditItem(undefined)
    setIsCreate(false)
  }

  const onRow = (data: ReturnOpportunityPipelineNomenclatorReadable) => ({
    onClick: () =>
      navigate(
        paths.admin.nomenclatures.opportunities.pipeline(String(data.id))
      )
  })

  const onDelete = () => {
    if (deleteItem)
      deleteOpportunityPipelineMutation.mutate({ path: { id: deleteItem } })
  }

  const onSortEnd = (
    action: (
      state: ReturnOpportunityPipelineNomenclatorReadable[]
    ) => ReturnOpportunityPipelineNomenclatorReadable[]
  ) => {
    setData((prev) => {
      const results = action(prev?.results || [])

      return {
        ...prev,
        count: prev?.count || 0,
        results: results || prev?.results
      }
    })

    setSorted(true)
  }

  const dndItems = data?.results?.map(({ id }) => id) || []

  const onSubmit = async (force = false) => {
    const values = await form.validateFields()

    if (!values.responsible && !force) {
      setShowResponsibleWarning(true)
      return
    }

    if (!values.responsible && force) {
      values.responsible = null
    }

    if (editItem) {
      updateMutation.mutate({ path: { id: editItem }, body: values })
    } else {
      createMutation.mutate({ body: values })
    }

    queryClient.refetchQueries()
    setShowResponsibleWarning(false)
    onCloseModal()
  }

  return (
    <>
      <DndTable
        disabled={!canManageCatalogs}
        rightChildren={
          <CatalogAddButton onClick={() => setIsCreate(true)}>
            {t('nomenclature.add_pipeline')}
          </CatalogAddButton>
        }
        onDragEnd={onSortEnd}
        items={dndItems}
        data={data}
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        onRow={onRow}
        rowKey="id"
        rowClassName="pointer"
        bordered
      />

      <FormModal
        form={form}
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={opportunitiesPipelinesRetrieveOptions}
        onCreate={opportunitiesPipelinesCreateMutation}
        onUpdate={opportunitiesPipelinesPartialUpdateMutation}
        onCloseModal={onCloseModal}
        onFinish={onSubmit}
      >
        <FormNomenclaturesOpportunityPipelines form={form} />
      </FormModal>

      <ConfirmModal
        open={showResponsibleWarning}
        header={t('warning')}
        okText={t('buttons.continue_anyway')}
        cancelText={t('buttons.cancel')}
        onSuccess={() => onSubmit(true)}
        onCancel={() => setShowResponsibleWarning(false)}
      >
        {t('pipeline_no_responsible_warning')}
      </ConfirmModal>

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCloseModal}
        confirmLoading={deleteOpportunityPipelineMutation.isPending}
      />
    </>
  )
}
